import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VacationCard } from "./VacationCard";
import { Button } from "@/components/ui/button";
import { Tag, Crown, MapPin, Plane } from "lucide-react";

interface SmartHomepageVacationsProps {
  onShowDeals: () => void;
  onShowLuxury: () => void;
  onShowRondreizen: () => void;
  onShowAllVacations: () => void;
}

export const SmartHomepageVacations = ({ 
  onShowDeals, 
  onShowLuxury, 
  onShowRondreizen, 
  onShowAllVacations 
}: SmartHomepageVacationsProps) => {
  const [deals, setDeals] = useState<any[]>([]);
  const [luxury, setLuxury] = useState<any[]>([]);
  const [rondreizen, setRondreizen] = useState<any[]>([]);
  const [allVacations, setAllVacations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSmartVacations = async () => {
    setLoading(true);
    
    try {
      // Get search performance data to understand what users are looking for
      const { data: searchPerformance } = await supabase
        .from('search_performance')
        .select('search_query, click_through_rate, total_searches')
        .gte('click_through_rate', 0.1)
        .gte('total_searches', 2)
        .order('click_through_rate', { ascending: false })
        .limit(10);

      // Extract popular countries/regions from successful searches
      const popularDestinations = new Set<string>();
      searchPerformance?.forEach(search => {
        const query = search.search_query.toLowerCase();
        // Extract common destination names
        const destinations = ['spanje', 'turkije', 'griekenland', 'italië', 'frankrijk', 'portugal', 'kroatië', 'duitsland'];
        destinations.forEach(dest => {
          if (query.includes(dest)) {
            popularDestinations.add(dest.charAt(0).toUpperCase() + dest.slice(1));
          }
        });
      });

      console.log('Popular destinations from search data:', Array.from(popularDestinations));

      // Fetch deals with preference for popular destinations
      let dealsQuery = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .lt('price', 500);

      if (popularDestinations.size > 0) {
        dealsQuery = dealsQuery.in('country', Array.from(popularDestinations));
      }

      const { data: dealsData } = await dealsQuery.limit(50);
      
      // Fallback to regular deals if no popular destination deals found
      if (!dealsData || dealsData.length < 10) {
        const { data: fallbackDeals } = await supabase
          .from('TUI')
          .select('*')
          .not('imageURL', 'is', null)
          .not('name', 'is', null)
          .not('price', 'is', null)
          .lt('price', 500)
          .limit(50);
        setDeals(shuffleArray(fallbackDeals || []).slice(0, 3));
      } else {
        setDeals(shuffleArray(dealsData).slice(0, 3));
      }

      // Fetch luxury vacations with smart selection
      let luxuryQuery = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .eq('rondreizen', false)
        .not('cruises', 'eq', 'true')
        .gte('price', 2000);

      if (popularDestinations.size > 0) {
        luxuryQuery = luxuryQuery.in('country', Array.from(popularDestinations));
      }

      const { data: luxuryData } = await luxuryQuery.limit(50);
      
      if (!luxuryData || luxuryData.length < 5) {
        const { data: fallbackLuxury } = await supabase
          .from('TUI')
          .select('*')
          .not('imageURL', 'is', null)
          .not('name', 'is', null)
          .not('price', 'is', null)
          .eq('rondreizen', false)
          .not('cruises', 'eq', 'true')
          .gte('price', 2000)
          .limit(50);
        setLuxury(shuffleArray(fallbackLuxury || []).slice(0, 3));
      } else {
        setLuxury(shuffleArray(luxuryData).slice(0, 3));
      }

      // Fetch rondreizen - these don't need smart selection as they're a specific category
      const { data: rondreizenData } = await supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .eq('rondreizen', true)
        .limit(50);

      setRondreizen(shuffleArray(rondreizenData || []).slice(0, 3));

      // Fetch all vacations with preference for popular destinations
      let allQuery = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null);

      if (popularDestinations.size > 0) {
        allQuery = allQuery.in('country', Array.from(popularDestinations));
      }

      const { data: allVacationsData } = await allQuery.limit(50);
      
      if (!allVacationsData || allVacationsData.length < 10) {
        const { data: fallbackAll } = await supabase
          .from('TUI')
          .select('*')
          .not('imageURL', 'is', null)
          .not('name', 'is', null)
          .limit(50);
        setAllVacations(shuffleArray(fallbackAll || []).slice(0, 3));
      } else {
        setAllVacations(shuffleArray(allVacationsData).slice(0, 3));
      }

      console.log('Smart homepage vacations loaded with popular destinations preference');
      
    } catch (error) {
      console.error('Error fetching smart vacations:', error);
      // Fallback to regular random selection
      await fetchRegularVacations();
    } finally {
      setLoading(false);
    }
  };

  const fetchRegularVacations = async () => {
    try {
      const { data: dealsData } = await supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .lt('price', 500)
        .limit(50);
      
      const { data: luxuryData } = await supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .eq('rondreizen', false)
        .not('cruises', 'eq', 'true')
        .gte('price', 2000)
        .limit(50);
      
      const { data: rondreizenData } = await supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .eq('rondreizen', true)
        .limit(50);
      
      const { data: allVacationsData } = await supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .limit(50);

      setDeals(shuffleArray(dealsData || []).slice(0, 3));
      setLuxury(shuffleArray(luxuryData || []).slice(0, 3));
      setRondreizen(shuffleArray(rondreizenData || []).slice(0, 3));
      setAllVacations(shuffleArray(allVacationsData || []).slice(0, 3));
    } catch (error) {
      console.error('Error fetching regular vacations:', error);
    }
  };

  const shuffleArray = (array: any[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    fetchSmartVacations();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Vakanties laden...</p>
      </div>
    );
  }

  const VacationSection = ({ 
    title, 
    vacations, 
    onShowAll, 
    buttonText, 
    icon: Icon,
    bgGradient = "from-orange-500 to-amber-500",
    textColor = "text-orange-600"
  }: {
    title: string;
    vacations: any[];
    onShowAll: () => void;
    buttonText: string;
    icon: any;
    bgGradient?: string;
    textColor?: string;
  }) => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className={`text-3xl font-bold ${textColor} flex items-center gap-3 justify-center font-poppins`}>
          <Icon className="h-8 w-8" />
          {title}
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {vacations.map((vacation) => (
          <VacationCard
            key={vacation['product ID']}
            vacation={vacation}
            user={null}
            isFavorite={false}
            onFavoriteChange={() => {}}
          />
        ))}
      </div>
      
      <div className="text-center">
        <Button 
          onClick={onShowAll}
          className={`bg-gradient-to-r ${bgGradient} hover:opacity-90 text-white px-8 py-3 text-lg font-medium rounded-lg shadow-lg transition-all duration-200 hover:shadow-xl transform hover:-translate-y-1`}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-16 mt-12">
      <VacationSection
        title="Ontdek de beste deals!"
        vacations={deals}
        onShowAll={onShowDeals}
        buttonText="Bekijk alle deals"
        icon={Tag}
        bgGradient="from-orange-500 to-red-500"
        textColor="text-orange-600"
      />
      
      <VacationSection
        title="Luxe vakanties"
        vacations={luxury}
        onShowAll={onShowLuxury}
        buttonText="Bekijk alle luxe vakanties"
        icon={Crown}
        bgGradient="from-amber-500 to-yellow-500"
        textColor="text-amber-600"
      />
      
      <VacationSection
        title="Bijzondere rondreizen"
        vacations={rondreizen}
        onShowAll={onShowRondreizen}
        buttonText="Bekijk alle rondreizen"
        icon={MapPin}
        bgGradient="from-blue-500 to-indigo-500"
        textColor="text-blue-600"
      />
      
      <VacationSection
        title="Ontdek het complete aanbod"
        vacations={allVacations}
        onShowAll={onShowAllVacations}
        buttonText="Bekijk alle vakanties"
        icon={Plane}
        bgGradient="from-green-500 to-teal-500"
        textColor="text-green-600"
      />
    </div>
  );
};
